/**
 * Public Link in Bio URLs are `/{finitlessProject}/{slug}` so the RPC can scope by project.
 * First segment must not collide with dashboard top-level routes (see reserved set).
 */

/** Same character class as Edge `PROJECT_RE` for `finitless_project`. */
export const LINK_IN_BIO_PROJECT_SEGMENT_RE = /^[a-zA-Z0-9_-]{1,128}$/;

/**
 * Lowercase first path segments reserved for the dashboard app (two-segment URLs like /menu/stores).
 */
export const LINK_IN_BIO_DASHBOARD_RESERVED_FIRST_SEGMENTS = new Set([
  "account",
  "ai-agents",
  "analyze",
  "campaigns",
  "delivery",
  "discounts",
  "download-app",
  "growth",
  "integrations",
  "menu",
  "orders",
  "overview",
  "password",
  "r",
  "reset-password",
  "settings",
  "support",
  "users",
]);

/**
 * @param {string} pathname
 * @returns {{ project: string; slug: string } | null}
 */
export function parsePublicLinkInBioPath(pathname) {
  const pathOnly = String(pathname || "").split("?")[0];
  const segments = pathOnly.split("/").filter(Boolean);
  if (segments.length !== 2) {
    return null;
  }
  const [projectSeg, slugSeg] = segments;
  const projectLower = projectSeg.toLowerCase();
  if (LINK_IN_BIO_DASHBOARD_RESERVED_FIRST_SEGMENTS.has(projectLower)) {
    return null;
  }
  if (!LINK_IN_BIO_PROJECT_SEGMENT_RE.test(projectSeg)) {
    return null;
  }
  const slug = String(slugSeg).trim().toLowerCase();
  if (!slug) {
    return null;
  }
  return { project: projectSeg.trim(), slug };
}

/**
 * @param {string} origin
 * @param {string} project
 * @param {string} slug
 * @returns {string}
 */
export function buildPublicLinkInBioUrl(origin, project, slug) {
  const base = String(origin || "").replace(/\/$/, "");
  const p = String(project || "").trim();
  const s = String(slug || "")
    .trim()
    .toLowerCase();
  if (!base || !p || !s) {
    return "";
  }
  return `${base}/${encodeURIComponent(p)}/${encodeURIComponent(s)}`;
}

/**
 * Base URL for shared link-in-bio links (marketing domain in production).
 * Set `VITE_LINK_IN_BIO_PUBLIC_BASE_URL` (e.g. https://www.finitless.com).
 * Falls back to `window.location.origin` when unset (local dev).
 */
export function getLinkInBioPublicBaseUrl() {
  if (typeof window === "undefined") {
    return "";
  }
  const explicit = import.meta.env.VITE_LINK_IN_BIO_PUBLIC_BASE_URL;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit).replace(/\/$/, "");
  }
  return window.location.origin.replace(/\/$/, "");
}
